<template>
  <div class="row game-settings-view">
    <!-- Header Section -->
    <div class="col-12 mb-4">
      <div class="d-flex flex-wrap justify-content-between align-items-center pb-3 border-bottom border-secondary">
        <div>
          <h2 class="text-white font-weight-bold mb-1">
            <i class="mdi mdi-cogs text-warning mr-2"></i>{{ $t('gameSettings.title') }}
          </h2>
          <p class="text-muted small mb-0">
            {{ $t('gameSettings.subtitle') }}
          </p>
        </div>
        <div class="d-flex align-items-center gap-2 mt-2 mt-md-0">
          <button
            class="btn btn-outline-warning font-weight-bold btn-sm mr-2"
            @click="activeTab = 'aiArchitect'"
          >
            <i class="mdi mdi-robot mr-1"></i> {{ isIndonesian ? 'Asisten Perancang AI' : 'AI Architect Copilot' }}
          </button>
          <span class="badge badge-outline-warning px-3 py-2 font-weight-bold">
            <i class="mdi mdi-shield-check mr-1"></i>{{ $t('gameSettings.engineActive') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs -->
    <div class="col-12 mb-4">
      <ul class="nav nav-tabs border-secondary flex-nowrap overflow-auto" role="tablist" style="scrollbar-width: thin;">
        <li class="nav-item flex-shrink-0">
          <a
            class="nav-link font-weight-bold text-nowrap px-3 py-2"
            style="font-size: 0.84rem;"
            :class="{ active: activeTab === 'terminology' }"
            href="#"
            @click.prevent="activeTab = 'terminology'"
          >
            <i class="mdi mdi-book-open-page-variant mr-1"></i>
            {{ $t('gameSettings.tabTerminology') }}
          </a>
        </li>
        <li class="nav-item flex-shrink-0">
          <a
            class="nav-link font-weight-bold text-nowrap px-3 py-2"
            style="font-size: 0.84rem;"
            :class="{ active: activeTab === 'engine' }"
            href="#"
            @click.prevent="activeTab = 'engine'"
          >
            <i class="mdi mdi-calculator-variant mr-1"></i>
            {{ $t('gameSettings.tabEngine') }}
          </a>
        </li>
        <li class="nav-item flex-shrink-0">
          <a
            class="nav-link font-weight-bold text-nowrap px-3 py-2"
            style="font-size: 0.84rem;"
            :class="{ active: activeTab === 'branding' }"
            href="#"
            @click.prevent="activeTab = 'branding'"
          >
            <i class="mdi mdi-palette-swatch mr-1"></i>
            {{ $t('gameSettings.tabBranding') }}
          </a>
        </li>
        <li class="nav-item flex-shrink-0">
          <a
            class="nav-link font-weight-bold text-nowrap px-3 py-2"
            style="font-size: 0.84rem;"
            :class="{ active: activeTab === 'wizard' }"
            href="#"
            @click.prevent="activeTab = 'wizard'"
          >
            <i class="mdi mdi-flag-checkered mr-1"></i>
            {{ $t('gameSettings.tabWizard') }}
          </a>
        </li>
        <li class="nav-item flex-shrink-0">
          <a
            class="nav-link font-weight-bold text-warning text-nowrap px-3 py-2"
            style="font-size: 0.84rem;"
            :class="{ active: activeTab === 'aiArchitect' }"
            href="#"
            @click.prevent="activeTab = 'aiArchitect'"
          >
            <i class="mdi mdi-robot mr-1"></i>
            {{ $t('gameSettings.tabAi') }}
          </a>
        </li>
      </ul>
    </div>

    <!-- TAB 1: TERMINOLOGY & ARCHITECTURE GUIDE -->
    <div class="col-12" v-if="activeTab === 'terminology'">
      <div class="row">
        <!-- Overview Banner -->
        <div class="col-12 mb-4">
          <div class="card border border-secondary shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div
                  class="rounded-circle mr-3 d-flex align-items-center justify-content-center bg-dark text-warning"
                  style="width: 48px; height: 48px; min-width: 48px; border: 1px solid rgba(255, 171, 0, 0.3);"
                >
                  <i class="mdi mdi-information-outline h4 mb-0"></i>
                </div>
                <div>
                  <h4 class="text-white font-weight-bold mb-1">{{ $t('gameSettings.mechanismTitle') }}</h4>
                  <p class="text-muted small mb-0">
                    {{ $t('gameSettings.mechanismDesc') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Terminology Cards Grid -->
        <div class="col-md-6 col-lg-4 mb-4">
          <div class="card h-100 border border-secondary">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="badge badge-success px-2 py-1 font-weight-bold">{{ $t('gameSettings.targetPositive') }}</span>
                  <i class="mdi mdi-trophy text-success h4 mb-0"></i>
                </div>
                <h5 class="text-white font-weight-bold">{{ $t('gameSettings.questsTitle') }}</h5>
                <p class="text-muted small"><strong>{{ $t('gameSettings.databaseLabel') }}:</strong> <code>rules</code> &amp; <code>user_rules</code></p>
                <p class="text-white-50 small">
                  {{ $t('gameSettings.questsCardDesc') }}
                </p>
                <div class="p-2 bg-dark rounded border border-secondary small text-muted mb-3">
                  <i class="mdi mdi-arrow-right text-success mr-1" aria-hidden="true"></i>{{ $t('gameSettings.questsFlow') }}
                </div>
              </div>
              <router-link :to="{ name: 'ListRewards' }" class="btn btn-outline-success btn-sm w-100 font-weight-bold">
                {{ $t('gameSettings.manageQuests') }}
              </router-link>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-lg-4 mb-4">
          <div class="card h-100 border border-secondary">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="badge badge-danger px-2 py-1 font-weight-bold">{{ $t('gameSettings.sopDiscipline') }}</span>
                  <i class="mdi mdi-shield-alert text-danger h4 mb-0" aria-hidden="true"></i>
                </div>
                <h5 class="text-white font-weight-bold">{{ $t('gameSettings.penaltiesTitle') }}</h5>
                <p class="text-muted small"><strong>{{ $t('gameSettings.databaseLabel') }}:</strong> <code>consequences</code> &amp; <code>user_consequences</code></p>
                <p class="text-white-50 small">
                  {{ $t('gameSettings.penaltiesCardDesc') }}
                </p>
                <div class="p-2 bg-dark rounded border border-secondary small text-muted mb-3">
                  <i class="mdi mdi-arrow-right text-danger mr-1" aria-hidden="true"></i>{{ $t('gameSettings.penaltiesFlow') }}
                </div>
              </div>
              <router-link :to="{ name: 'ListConsequence' }" class="btn btn-outline-danger btn-sm w-100 font-weight-bold">
                {{ $t('gameSettings.managePenalties') }}
              </router-link>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-lg-4 mb-4">
          <div class="card h-100 border border-secondary">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="badge badge-info px-2 py-1 font-weight-bold">{{ $t('gameSettings.remediation') }}</span>
                  <i class="mdi mdi-hand-heart text-info h4 mb-0" aria-hidden="true"></i>
                </div>
                <h5 class="text-white font-weight-bold">{{ $t('gameSettings.compensationsTitle') }}</h5>
                <p class="text-muted small"><strong>{{ $t('gameSettings.databaseLabel') }}:</strong> <code>compensations</code> &amp; <code>user_compensations</code></p>
                <p class="text-white-50 small">
                  {{ $t('gameSettings.compensationsCardDesc') }}
                </p>
                <div class="p-2 bg-dark rounded border border-secondary small text-muted mb-3">
                  <i class="mdi mdi-arrow-right text-info mr-1" aria-hidden="true"></i>{{ $t('gameSettings.compensationsFlow') }}
                </div>
              </div>
              <router-link :to="{ name: 'Compensation' }" class="btn btn-outline-info btn-sm w-100 font-weight-bold">
                {{ $t('gameSettings.manageCompensations') }}
              </router-link>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-lg-4 mb-4">
          <div class="card h-100 border border-secondary">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="badge badge-warning text-dark px-2 py-1 font-weight-bold">{{ $t('gameSettings.rewardShopBadge') }}</span>
                  <i class="mdi mdi-storefront text-warning h4 mb-0" aria-hidden="true"></i>
                </div>
                <h5 class="text-white font-weight-bold">{{ $t('gameSettings.rewardShopTitle') }}</h5>
                <p class="text-muted small"><strong>{{ $t('gameSettings.databaseLabel') }}:</strong> <code>redeems</code> &amp; <code>user_redeems</code></p>
                <p class="text-white-50 small">
                  {{ $t('gameSettings.rewardShopCardDesc') }}
                </p>
                <div class="p-2 bg-dark rounded border border-secondary small text-muted mb-3">
                  <i class="mdi mdi-arrow-right text-warning mr-1" aria-hidden="true"></i>{{ $t('gameSettings.rewardShopFlow') }}
                </div>
              </div>
              <router-link to="/redeem" class="btn btn-outline-warning btn-sm w-100 font-weight-bold">
                {{ $t('gameSettings.manageShop') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 2: ENGINE MECHANICS & PARAMETERS -->
    <div class="col-12" v-if="activeTab === 'engine'">
      <div class="row">
        <!-- Interactive Engine Configuration Panel -->
        <div class="col-12 mb-4">
          <div class="card border border-warning shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <div>
                  <h4 class="text-white font-weight-bold mb-1">
                    <i class="mdi mdi-tune-vertical text-warning mr-2" aria-hidden="true"></i>{{ $t('gameSettings.engineTuningTitle') }}
                  </h4>
                  <p class="text-muted small mb-0">
                    {{ $t('gameSettings.engineTuningSubtitle') }}
                  </p>
                </div>
                <button class="btn btn-warning text-dark font-weight-bold mt-2 mt-md-0" @click="saveEngineConfig">
                  <i class="mdi mdi-content-save mr-1" aria-hidden="true"></i>{{ $t('gameSettings.saveEngineConfig') }}
                </button>
              </div>

              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold">{{ $t('gameSettings.baseXpPerLevel') }}</label>
                  <select class="form-control" v-model.number="engineConfig.baseXp">
                    <option :value="50">{{ $t('gameSettings.baseXp50') }}</option>
                    <option :value="100">{{ $t('gameSettings.baseXp100') }}</option>
                    <option :value="150">{{ $t('gameSettings.baseXp150') }}</option>
                    <option :value="200">{{ $t('gameSettings.baseXp200') }}</option>
                  </select>
                </div>

                <div class="col-md-4 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold">{{ $t('gameSettings.xpCurveLabel') }}</label>
                  <select class="form-control" v-model="engineConfig.curveType">
                    <option value="linear">{{ $t('gameSettings.xpCurveLinear') }}</option>
                    <option value="moderate">{{ $t('gameSettings.xpCurveModerate') }}</option>
                    <option value="exponential">{{ $t('gameSettings.xpCurveExponential') }}</option>
                  </select>
                </div>

                <div class="col-md-4 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold">{{ $t('gameSettings.dailyGoldCapLabel') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" v-model.number="engineConfig.dailyGoldCap" min="50" step="25" />
                    <div class="input-group-append">
                      <span class="input-group-text bg-dark border-secondary text-warning font-weight-bold">{{ $t('gameSettings.goldPerDayUnit') }}</span>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold">{{ $t('gameSettings.streakMultiplierLabel') }}</label>
                  <select class="form-control" v-model.number="engineConfig.streak7Bonus">
                    <option :value="15">+15% {{ $t('gameSettings.goldMultiplierSuffix') }}</option>
                    <option :value="20">+20% {{ $t('gameSettings.goldMultiplierSuffix') }}</option>
                    <option :value="25">+25% {{ $t('gameSettings.goldMultiplierStandard') }}</option>
                    <option :value="30">+30% {{ $t('gameSettings.goldMultiplierHigh') }}</option>
                  </select>
                </div>

                <div class="col-md-4 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold">{{ $t('gameSettings.kudosAllowanceLabel') }}</label>
                  <select class="form-control" v-model.number="engineConfig.kudosAllowance">
                    <option :value="3">{{ $t('gameSettings.kudosOption3') }}</option>
                    <option :value="5">{{ $t('gameSettings.kudosOption5') }}</option>
                    <option :value="10">{{ $t('gameSettings.kudosOption10') }}</option>
                  </select>
                </div>

                <div class="col-md-4 mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold">{{ $t('gameSettings.antiCheatToleranceLabel') }}</label>
                  <select class="form-control" v-model="engineConfig.antiCheatLevel">
                    <option value="lenient">{{ $t('gameSettings.antiCheatLenient') }}</option>
                    <option value="balanced">{{ $t('gameSettings.antiCheatBalanced') }}</option>
                    <option value="strict">{{ $t('gameSettings.antiCheatStrict') }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dynamic Progression Matrix Table -->
        <div class="col-md-6 mb-4">
          <div class="card h-100 border border-secondary">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <div class="d-flex align-items-center">
                  <i class="mdi mdi-stairs text-warning display-4 mr-3" aria-hidden="true"></i>
                  <div>
                    <h4 class="text-white font-weight-bold mb-1">{{ $t('gameSettings.xpMatrixTitle') }}</h4>
                    <p class="text-muted small mb-0">{{ $t('gameSettings.xpMatrixSubtitle') }}</p>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-hover text-white">
                  <thead>
                    <tr class="text-muted small text-uppercase">
                      <th>{{ $t('gameSettings.colTierLevel') }}</th>
                      <th>{{ $t('gameSettings.colRequiredXp') }}</th>
                      <th>{{ $t('gameSettings.colCumulativeXp') }}</th>
                      <th>{{ $t('gameSettings.colLevelUpReward') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in progressionMatrix" :key="row.tier" style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                      <td><span class="badge" :class="row.badgeClass">{{ row.tier }}</span></td>
                      <td>{{ formatLocaleNumber(row.xpReq) }} XP</td>
                      <td>{{ formatLocaleNumber(row.cumXp) }} XP</td>
                      <td class="text-warning font-weight-bold">{{ row.goldBonus }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Daily Streak & Kudos System Card -->
        <div class="col-md-6 mb-4">
          <div class="card h-100 border border-secondary">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <i class="mdi mdi-fire text-danger display-4 mr-3" aria-hidden="true"></i>
                <div>
                  <h4 class="text-white font-weight-bold mb-1">{{ $t('gameSettings.dailyStreakTitle') }}</h4>
                  <p class="text-muted small mb-0">{{ $t('gameSettings.dailyStreakSubtitle') }}</p>
                </div>
              </div>

              <div class="list-group mb-3">
                <div class="list-group-item bg-dark border-secondary d-flex justify-content-between align-items-center">
                  <div>
                    <strong class="text-white"><i class="mdi mdi-calendar-today mr-2 text-muted" aria-hidden="true"></i>{{ $t('gameSettings.days1To2') }}</strong>
                    <div class="text-muted small">{{ $t('gameSettings.days1To2Desc') }}</div>
                  </div>
                  <span class="badge badge-outline-secondary px-3 py-2 font-weight-bold">1.0x ({{ $t('gameSettings.normalRate') }})</span>
                </div>
                <div class="list-group-item bg-dark border-secondary d-flex justify-content-between align-items-center">
                  <div>
                    <strong class="text-warning"><i class="mdi mdi-fire mr-2 text-warning" aria-hidden="true"></i>{{ $t('gameSettings.days3To6') }}</strong>
                    <div class="text-muted small">{{ $t('gameSettings.days3To6Desc') }}</div>
                  </div>
                  <span class="badge badge-warning text-dark px-3 py-2 font-weight-bold">1.10x (+10% Gold)</span>
                </div>
                <div class="list-group-item bg-dark border-secondary d-flex justify-content-between align-items-center">
                  <div>
                    <strong class="text-danger"><i class="mdi mdi-fire display-5 mr-2 text-danger" aria-hidden="true"></i>{{ $t('gameSettings.days7Plus') }}</strong>
                    <div class="text-muted small">{{ $t('gameSettings.days7PlusDesc') }}</div>
                  </div>
                  <span class="badge badge-danger px-3 py-2 font-weight-bold">1.{{ engineConfig.streak7Bonus }}x (+{{ engineConfig.streak7Bonus }}% Gold)</span>
                </div>
              </div>

              <!-- Kudos System -->
              <div class="p-3 bg-dark rounded border border-warning">
                <div class="d-flex align-items-center">
                  <i class="mdi mdi-star-face text-warning h2 mr-3 mb-0" aria-hidden="true"></i>
                  <div>
                    <strong class="text-white">{{ $t('gameSettings.kudosSystemTitle') }}</strong>
                    <p class="text-muted small mb-0">
                      {{ $t('gameSettings.kudosSystemDesc', { allowance: engineConfig.kudosAllowance, gold: engineConfig.kudosGold }) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Level Simulator Interactive Tool -->
        <div class="col-12 mb-4">
          <div class="card border border-primary">
            <div class="card-body">
              <h4 class="text-white font-weight-bold mb-1">
                <i class="mdi mdi-calculator text-primary mr-2" aria-hidden="true"></i>{{ $t('gameSettings.xpSimulatorTitle') }}
              </h4>
              <p class="text-muted small mb-4">{{ $t('gameSettings.xpSimulatorSubtitle') }}</p>
              
              <div class="row align-items-center">
                <div class="col-md-4">
                  <label class="text-muted small text-uppercase font-weight-bold">{{ $t('gameSettings.enterTotalXpLabel') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" v-model.number="simulatorXp" min="0" step="50" />
                    <div class="input-group-append">
                      <span class="input-group-text bg-secondary border-secondary text-white font-weight-bold">XP</span>
                    </div>
                  </div>
                </div>
                <div class="col-md-8 mt-3 mt-md-0">
                  <div class="row text-center g-2">
                    <div class="col-sm-4">
                      <div class="bg-dark p-3 rounded border border-secondary">
                        <small class="text-muted d-block text-uppercase font-weight-bold" style="font-size: 10px;">{{ $t('gameSettings.calculatedRankLabel') }}</small>
                        <span class="h3 font-weight-bold text-warning mb-0 d-block">{{ $t('gameSettings.levelValue', { level: calculatedSim.level }) }}</span>
                      </div>
                    </div>
                    <div class="col-sm-4">
                      <div class="bg-dark p-3 rounded border border-secondary">
                        <small class="text-muted d-block text-uppercase font-weight-bold" style="font-size: 10px;">{{ $t('gameSettings.currentTierXpLabel') }}</small>
                        <span class="h4 font-weight-bold text-white mb-0 d-block">{{ calculatedSim.currentXp }} / {{ calculatedSim.xpNext }}</span>
                      </div>
                    </div>
                    <div class="col-sm-4">
                      <div class="bg-dark p-3 rounded border border-secondary">
                        <small class="text-muted d-block text-uppercase font-weight-bold" style="font-size: 10px;">{{ $t('gameSettings.toNextLevelLabel', { level: calculatedSim.level + 1 }) }}</small>
                        <span class="h4 font-weight-bold text-success mb-0 d-block">{{ calculatedSim.percent }}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 3: AI GAME ARCHITECT & COPILOT -->
    <div class="col-12" v-if="activeTab === 'aiArchitect'">
      <div class="row">
        <!-- Hero AI Card -->
        <div class="col-12 mb-4">
          <div class="card border border-warning" style="background: linear-gradient(135deg, rgba(255, 171, 0, 0.1) 0%, rgba(24, 24, 27, 0.95) 100%);">
            <div class="card-body p-4">
              <div class="d-flex align-items-center justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-3 mb-md-0">
                  <div class="avatar-circle mr-3" style="width: 56px; height: 56px; background: rgba(255, 171, 0, 0.2); color: #ffab00; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 28px; border: 1px solid rgba(255, 171, 0, 0.4);">
                    <i class="mdi mdi-robot" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h3 class="text-white font-weight-bold mb-1">{{ $t('gameSettings.aiBannerTitle') }}</h3>
                    <p class="text-muted small mb-0" style="max-width: 650px;">
                      {{ $t('gameSettings.aiBannerDesc') }}
                    </p>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-3" v-if="activeBlueprint">
                  <div class="text-center px-3 py-2 bg-dark rounded border border-secondary">
                    <small class="text-muted d-block" style="font-size: 10px;">{{ $t('gameSettings.aiHealthScore') }}</small>
                    <span class="text-success font-weight-bold h4 mb-0">{{ activeBlueprint.healthScore }}/100</span>
                  </div>
                  <div class="text-center px-3 py-2 bg-dark rounded border border-secondary">
                    <small class="text-muted d-block" style="font-size: 10px;">{{ $t('gameSettings.aiBurnoutRisk') }}</small>
                    <span class="badge badge-success font-weight-bold mt-1">{{ $t('gameSettings.lowOptimal') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Archetype Selection & Prompt Box -->
        <div class="col-12 mb-4">
          <div class="card border border-secondary">
            <div class="card-body">
              <label class="text-muted small text-uppercase font-weight-bold d-block mb-2">{{ $t('gameSettings.aiSelectIndustry') }}</label>
              <div class="d-flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  class="btn btn-sm mr-2 mb-2 font-weight-bold"
                  :class="selectedArchetype === 'tech' ? 'btn-primary' : 'btn-outline-secondary text-white'"
                  @click="selectArchetype('tech')"
                >
                  <i class="mdi mdi-laptop mr-1" aria-hidden="true"></i> {{ $t('gameSettings.archTech') }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm mr-2 mb-2 font-weight-bold"
                  :class="selectedArchetype === 'sales' ? 'btn-primary' : 'btn-outline-secondary text-white'"
                  @click="selectArchetype('sales')"
                >
                  <i class="mdi mdi-chart-line mr-1" aria-hidden="true"></i> {{ $t('gameSettings.archSales') }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm mr-2 mb-2 font-weight-bold"
                  :class="selectedArchetype === 'support' ? 'btn-primary' : 'btn-outline-secondary text-white'"
                  @click="selectArchetype('support')"
                >
                  <i class="mdi mdi-headset mr-1" aria-hidden="true"></i> {{ $t('gameSettings.archSupport') }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm mr-2 mb-2 font-weight-bold"
                  :class="selectedArchetype === 'creative' ? 'btn-primary' : 'btn-outline-secondary text-white'"
                  @click="selectArchetype('creative')"
                >
                  <i class="mdi mdi-palette mr-1" aria-hidden="true"></i> {{ $t('gameSettings.archCreative') }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm mb-2 font-weight-bold"
                  :class="selectedArchetype === 'operations' ? 'btn-primary' : 'btn-outline-secondary text-white'"
                  @click="selectArchetype('operations')"
                >
                  <i class="mdi mdi-truck-delivery mr-1" aria-hidden="true"></i> {{ $t('gameSettings.archOperations') }}
                </button>
              </div>

              <!-- Prompt Input -->
              <div class="form-group mb-2">
                <label class="text-muted small text-uppercase font-weight-bold d-block mb-1">{{ $t('gameSettings.aiCustomPrompt') }}</label>
                <div class="d-flex gap-2 flex-wrap mb-2">
                  <button
                    v-for="chip in promptPresets"
                    :key="chip.key"
                    type="button"
                    class="badge badge-dark border border-secondary text-light px-2 py-1 mr-1"
                    style="cursor: pointer;"
                    @click="setPrompt(chip.prompt)"
                  >
                    <i :class="chip.icon + ' mr-1 text-warning'" aria-hidden="true"></i>{{ chip.label }}
                  </button>
                </div>
                <textarea
                  class="form-control"
                  rows="2"
                  v-model="aiPrompt"
                  :placeholder="$t('gameSettings.aiPromptPlaceholder')"
                ></textarea>
              </div>

              <div class="d-flex justify-content-end">
                <button
                  type="button"
                  class="btn btn-warning text-dark font-weight-bold px-4 py-2"
                  @click="generateAiBlueprint"
                  :disabled="isGeneratingAi"
                >
                  <span class="spinner-border spinner-border-sm mr-1" v-if="isGeneratingAi"></span>
                  <i class="mdi mdi-creation mr-1" aria-hidden="true" v-else></i>
                  {{ isGeneratingAi ? $t('gameSettings.aiGenerating') : $t('gameSettings.aiGenerateBtn') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- AI Generated Blueprint Output -->
        <div class="col-12 mb-4" v-if="activeBlueprint">
          <div class="card border border-success">
            <div class="card-header bg-dark d-flex justify-content-between align-items-center flex-wrap py-3 border-secondary">
              <div>
                <span class="badge badge-success text-dark font-weight-bold mr-2">{{ $t('gameSettings.aiBlueprintReady') }}</span>
                <strong class="text-white">{{ activeBlueprint.title }}</strong>
                <span class="text-muted small ml-2 d-none d-md-inline">({{ activeBlueprint.archetypeLabel }})</span>
              </div>
              <div class="d-flex gap-2 mt-2 mt-md-0">
                <button class="btn btn-sm btn-outline-info mr-2" @click="copyBlueprintJson">
                  <i class="mdi mdi-content-copy mr-1" aria-hidden="true"></i> {{ $t('gameSettings.copyJsonBtn') }}
                </button>
                <button
                  class="btn btn-sm btn-primary font-weight-bold mr-2"
                  @click="applyAiEngineSettings"
                >
                  <i class="mdi mdi-tune mr-1" aria-hidden="true"></i> {{ $t('gameSettings.aiApplyEngineBtn') }}
                </button>
                <button
                  class="btn btn-sm btn-success text-dark font-weight-bold"
                  @click="deployBlueprintToWorkspace"
                  :disabled="isDeploying"
                >
                  <span class="spinner-border spinner-border-sm mr-1" v-if="isDeploying"></span>
                  <i class="mdi mdi-rocket-launch mr-1" aria-hidden="true" v-else></i>
                  {{ isDeploying ? $t('gameSettings.aiDeploying') : $t('gameSettings.aiDeployRulesBtn') }}
                </button>
              </div>
            </div>

            <div class="card-body">
              <!-- Engine Parameter Summary -->
              <div class="p-3 bg-dark rounded border border-secondary mb-4">
                <h6 class="text-white font-weight-bold mb-2">
                  <i class="mdi mdi-cog-sync text-warning mr-1" aria-hidden="true"></i> {{ $t('gameSettings.recommendedEngineParams') }}
                </h6>
                <div class="row text-center g-2">
                  <div class="col-6 col-md-3">
                    <small class="text-muted d-block" style="font-size: 11px;">{{ $t('gameSettings.baseXpShort') }}</small>
                    <strong class="text-white">{{ activeBlueprint.engine.baseXp }} {{ $t('gameSettings.xpPerLvlUnit') }}</strong>
                  </div>
                  <div class="col-6 col-md-3">
                    <small class="text-muted d-block" style="font-size: 11px;">{{ $t('gameSettings.dailyGoldCapShort') }}</small>
                    <strong class="text-warning">{{ activeBlueprint.engine.dailyGoldCap }} Gold</strong>
                  </div>
                  <div class="col-6 col-md-3">
                    <small class="text-muted d-block" style="font-size: 11px;">{{ $t('gameSettings.streakMultiplierShort') }}</small>
                    <strong class="text-danger">+{{ activeBlueprint.engine.streak7Bonus }}% ({{ $t('gameSettings.days7Short') }})</strong>
                  </div>
                  <div class="col-6 col-md-3">
                    <small class="text-muted d-block" style="font-size: 11px;">{{ $t('gameSettings.antiCheatShort') }}</small>
                    <strong class="text-info">{{ formatAntiCheatLabel(activeBlueprint.engine.antiCheatLevel) }}</strong>
                  </div>
                </div>
              </div>

              <!-- Quests Suite -->
              <div class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="text-white font-weight-bold mb-0">
                    <i class="mdi mdi-trophy text-success mr-1" aria-hidden="true"></i> {{ $t('gameSettings.questsSuiteTitle', { count: activeBlueprint.quests.length }) }}
                  </h6>
                  <span class="badge badge-outline-success font-weight-medium">{{ $t('gameSettings.productivityIncentive') }}</span>
                </div>
                <div class="row">
                  <div class="col-md-4 mb-3" v-for="(q, idx) in activeBlueprint.quests" :key="idx">
                    <div class="p-3 bg-dark rounded border border-secondary h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="badge badge-secondary small text-uppercase">{{ q.frequency }}</span>
                          <span class="text-warning font-weight-bold"><i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>+{{ q.gold }} Gold</span>
                        </div>
                        <h6 class="text-white font-weight-bold mb-1">{{ q.name }}</h6>
                        <p class="text-muted small mb-2">{{ q.description }}</p>
                      </div>
                      <div class="text-muted small border-top border-secondary pt-2 mt-2">
                        <i class="mdi mdi-file-check-outline mr-1 text-info" aria-hidden="true"></i> {{ $t('gameSettings.evidenceLabel') }}: {{ q.evidence }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Penalties & Compensations -->
              <div class="row mb-4">
                <div class="col-md-6 mb-3 mb-md-0">
                  <h6 class="text-white font-weight-bold mb-2">
                    <i class="mdi mdi-shield-alert text-danger mr-1" aria-hidden="true"></i> {{ $t('gameSettings.sopPenaltiesTitle') }}
                  </h6>
                  <div class="p-3 bg-dark rounded border border-secondary h-100">
                    <div v-for="(c, cIdx) in activeBlueprint.consequences" :key="cIdx" class="mb-3 pb-2 border-bottom border-secondary last-border-0">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <strong class="text-white small">{{ c.name }}</strong>
                        <span class="badge badge-danger">-{{ c.gold }} Gold</span>
                      </div>
                      <small class="text-muted d-block">{{ c.description }}</small>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <h6 class="text-white font-weight-bold mb-2">
                    <i class="mdi mdi-hand-heart text-info mr-1" aria-hidden="true"></i> {{ $t('gameSettings.recoveryTasksTitle') }}
                  </h6>
                  <div class="p-3 bg-dark rounded border border-secondary h-100">
                    <div v-for="(comp, compIdx) in activeBlueprint.compensations" :key="compIdx" class="mb-3 pb-2 border-bottom border-secondary last-border-0">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <strong class="text-white small">{{ comp.name }}</strong>
                        <span class="badge badge-success">{{ $t('gameSettings.recoverInfraction') }}</span>
                      </div>
                      <small class="text-muted d-block">{{ comp.description }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Reward Shop Perks -->
              <div>
                <h6 class="text-white font-weight-bold mb-2">
                  <i class="mdi mdi-storefront text-warning mr-1" aria-hidden="true"></i> {{ $t('gameSettings.recommendedShopItems') }}
                </h6>
                <div class="row">
                  <div class="col-md-4 mb-2" v-for="(r, rIdx) in activeBlueprint.rewards" :key="rIdx">
                    <div class="p-3 bg-dark rounded border border-secondary d-flex justify-content-between align-items-center">
                      <div>
                        <strong class="text-white small d-block">{{ r.name }}</strong>
                        <small class="text-muted">{{ r.category }}</small>
                      </div>
                      <span class="badge badge-warning text-dark font-weight-bold">
                        <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i> {{ r.gold }} Gold
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 4: COMPANY PROFILE & BRANDING -->
    <div class="col-12" v-if="activeTab === 'branding'">
      <div class="row">
        <div class="col-md-7 mb-4">
          <div class="card border border-secondary">
            <div class="card-body">
              <h4 class="text-white font-weight-bold mb-2">
                <i class="mdi mdi-domain mr-2 text-warning" aria-hidden="true"></i>{{ $t('gameSettings.brandingTitle') }}
              </h4>
              <p class="text-muted small mb-4">
                {{ $t('gameSettings.brandingSubtitle') }}
              </p>

              <form @submit.prevent="saveCompanyProfile">
                <div class="form-group mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold mb-1 d-block" for="companyNameInput">{{ $t('gameSettings.companyNameLabel') }}</label>
                  <input
                    id="companyNameInput"
                    type="text"
                    class="form-control"
                    v-model="companyForm.name"
                    :placeholder="$t('gameSettings.companyNamePlaceholder')"
                    required
                  />
                </div>

                <div class="form-group mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold mb-1 d-block" for="companyThemeColorText">{{ $t('gameSettings.themeColorLabel') }}</label>
                  <div class="d-flex align-items-center">
                    <input
                      type="color"
                      class="form-control mr-3"
                      style="width: 52px; height: 40px; padding: 2px; cursor: pointer;"
                      v-model="companyForm.theme_color"
                      :aria-label="$t('gameSettings.themeColorLabel')"
                    />
                    <input
                      id="companyThemeColorText"
                      type="text"
                      class="form-control"
                      v-model="companyForm.theme_color"
                      placeholder="#ffab00"
                    />
                  </div>
                </div>

                <div class="form-group mb-3">
                  <label class="text-muted small text-uppercase font-weight-bold mb-1 d-block" for="companyWelcomeMsg">{{ $t('gameSettings.welcomeMessageLabel') }}</label>
                  <textarea
                    id="companyWelcomeMsg"
                    class="form-control"
                    rows="3"
                    v-model="companyForm.welcome_message"
                    :placeholder="$t('gameSettings.welcomeMessagePlaceholder')"
                  ></textarea>
                </div>

                <div class="form-group mb-4">
                  <label class="text-muted small text-uppercase font-weight-bold mb-1 d-block" for="companyLogoInput">{{ $t('gameSettings.companyLogoLabel') }}</label>
                  <input
                    type="file"
                    class="form-control-file text-muted"
                    id="companyLogoInput"
                    accept="image/*"
                    @change="handleLogoChange"
                  />
                  <small class="text-muted d-block mt-1">{{ $t('gameSettings.logoFormatHint') }}</small>
                </div>

                <div class="d-flex justify-content-end">
                  <button
                    type="submit"
                    class="btn btn-primary px-4 py-2 font-weight-bold"
                    :disabled="isSaving"
                  >
                    <span class="spinner-border spinner-border-sm mr-1" v-if="isSaving"></span>
                    <i class="mdi mdi-content-save mr-1" aria-hidden="true" v-else></i>
                    {{ isSaving ? $t('common.saving') : $t('common.save') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Live Preview Card -->
        <div class="col-md-5 mb-4">
          <div class="card border border-secondary">
            <div class="card-body">
              <h5 class="text-muted small text-uppercase font-weight-bold mb-3">
                <i class="mdi mdi-cellphone-link mr-2" aria-hidden="true"></i>{{ $t('gameSettings.livePreviewTitle') }}
              </h5>
              
              <div class="p-3 rounded border" :style="{ borderColor: companyForm.theme_color || '#ffab00', background: '#12141a' }">
                <!-- Mock Header -->
                <div class="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom border-secondary">
                  <div class="d-flex align-items-center">
                    <img
                      v-if="logoPreview"
                      :src="logoPreview"
                      alt="Logo"
                      class="rounded mr-2"
                      style="width: 32px; height: 32px; object-fit: contain; background: #fff;"
                    />
                    <div
                      v-else
                      class="rounded mr-2 d-flex align-items-center justify-content-center font-weight-bold"
                      :style="{ width: '32px', height: '32px', background: companyForm.theme_color || '#ffab00', color: '#000' }"
                    >
                      {{ (companyForm.name || 'G')[0] }}
                    </div>
                    <div>
                      <strong class="text-white d-block" style="font-size: 13px;">{{ companyForm.name || $t('gameSettings.defaultCompanyName') }}</strong>
                      <span class="badge badge-success text-dark font-weight-bold" style="font-size: 9px;">{{ $t('gameSettings.activeRealmBadge') }}</span>
                    </div>
                  </div>
                  <span class="badge badge-warning text-dark font-weight-bold">
                    <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>350 Gold
                  </span>
                </div>

                <!-- Mock Welcome Banner -->
                <div class="p-3 rounded mb-3" :style="{ background: (companyForm.theme_color || '#ffab00') + '22', borderLeft: '4px solid ' + (companyForm.theme_color || '#ffab00') }">
                  <div class="small font-weight-bold text-white mb-1">
                    <i class="mdi mdi-bullhorn mr-1" :style="{ color: companyForm.theme_color || '#ffab00' }" aria-hidden="true"></i>{{ $t('gameSettings.commanderBriefingLabel') }}
                  </div>
                  <div class="text-light small font-italic">
                    "{{ companyForm.welcome_message || $t('gameSettings.defaultWelcomePreview') }}"
                  </div>
                </div>

                <!-- Mock Stats -->
                <div class="row text-center g-2">
                  <div class="col-6">
                    <div class="bg-dark p-2 rounded border border-secondary">
                      <small class="text-muted d-block" style="font-size: 10px;">{{ $t('gameSettings.levelLabel') }}</small>
                      <strong class="text-warning">{{ $t('gameSettings.levelValue', { level: 3 }) }}</strong>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="bg-dark p-2 rounded border border-secondary">
                      <small class="text-muted d-block" style="font-size: 10px;">{{ $t('gameSettings.dailyStreakLabel') }}</small>
                      <strong class="text-danger"><i class="mdi mdi-fire" aria-hidden="true"></i> 5 {{ $t('gameSettings.daysUnit') }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 5: SETUP CHECKLIST WIZARD -->
    <div class="col-12" v-if="activeTab === 'wizard'">
      <div class="card border border-secondary">
        <div class="card-body">
          <h4 class="text-white font-weight-bold mb-2">
            <i class="mdi mdi-check-all text-warning mr-2"></i>{{ isIndonesian ? 'Panduan Penyiapan Sistem: 5 Langkah Utama' : 'System Setup Checklist: 5 Core Steps' }}
          </h4>
          <p class="text-muted small mb-4">
            {{ isIndonesian ? 'Ikuti 5 tahapan berurutan ini agar alur operasional Gamiteam berjalan mulus untuk seluruh anggota:' : 'Follow these sequential stages to establish a high-performing employee recognition loop:' }}
          </p>

          <div class="timeline-steps">
            <!-- Step 1 -->
            <div class="d-flex mb-4">
              <div class="mr-3">
                <div class="badge badge-primary rounded-circle p-3 d-flex align-items-center justify-content-center font-weight-bold" style="width: 44px; height: 44px; font-size: 16px;">
                  1
                </div>
              </div>
              <div class="flex-grow-1 border-bottom border-secondary pb-3">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <h5 class="text-white font-weight-bold mb-1">{{ isIndonesian ? 'Buat Divisi / Unit Kerja Tim' : 'Establish Divisions & Work Units' }}</h5>
                  <router-link :to="{ name: 'CreateSquad' }" class="btn btn-outline-primary btn-sm mb-1">
                    <i class="mdi mdi-plus mr-1"></i>{{ isIndonesian ? 'Buat Divisi Sekarang' : 'Create Division Now' }}
                  </router-link>
                </div>
                <p class="text-muted small mb-0">
                  {{ isIndonesian ? 'Fondasi Utama: Anggota tim membutuhkan penempatan divisi seperti Rekayasa, Pemasaran, atau Penjualan untuk pelacakan kinerja dan papan peringkat.' : 'Core foundation: Team members require division assignment for task tracking and division leaderboards.' }}
                </p>
              </div>
            </div>

            <!-- Step 2 -->
            <div class="d-flex mb-4">
              <div class="mr-3">
                <div class="badge badge-warning text-dark rounded-circle p-3 d-flex align-items-center justify-content-center font-weight-bold" style="width: 44px; height: 44px; font-size: 16px;">
                  2
                </div>
              </div>
              <div class="flex-grow-1 border-bottom border-secondary pb-3">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <h5 class="text-white font-weight-bold mb-1">{{ isIndonesian ? 'Daftarkan Akun Anggota Tim (Karyawan)' : 'Register Team Member Accounts' }}</h5>
                  <router-link :to="{ name: 'CreatePlayer' }" class="btn btn-outline-warning btn-sm mb-1 font-weight-bold">
                    <i class="mdi mdi-account-plus mr-1"></i>{{ isIndonesian ? 'Tambah Anggota' : 'Add Member' }}
                  </router-link>
                </div>
                <p class="text-muted small mb-0">
                  {{ isIndonesian ? 'Tambahkan akun karyawan ke dalam divisi yang relevan agar mereka dapat masuk ke portal karyawan dan menerima misi harian.' : 'Onboard employees into divisions so they can access tasks, progress through levels, and accumulate gold.' }}
                </p>
              </div>
            </div>

            <!-- Step 3 -->
            <div class="d-flex mb-4">
              <div class="mr-3">
                <div class="badge badge-success rounded-circle p-3 d-flex align-items-center justify-content-center font-weight-bold" style="width: 44px; height: 44px; font-size: 16px;">
                  3
                </div>
              </div>
              <div class="flex-grow-1 border-bottom border-secondary pb-3">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <h5 class="text-white font-weight-bold mb-1">{{ isIndonesian ? 'Rancang Aturan Misi & Target KPI' : 'Design Target Tasks & KPIs' }}</h5>
                  <router-link :to="{ name: 'CreateReward' }" class="btn btn-outline-success btn-sm mb-1 font-weight-bold">
                    <i class="mdi mdi-trophy-plus mr-1"></i>{{ isIndonesian ? 'Buat Misi & Target KPI' : 'Create Task' }}
                  </router-link>
                </div>
                <p class="text-muted small mb-0">
                  {{ isIndonesian ? 'Tentukan sasaran KPI atau aksi harian yang menghasilkan Gold dan XP saat bukti diunggah dan diverifikasi Manajer.' : 'Define positive actionable milestones that award gold and XP upon evidence verification.' }}
                </p>
              </div>
            </div>

            <!-- Step 4 -->
            <div class="d-flex mb-4">
              <div class="mr-3">
                <div class="badge badge-danger rounded-circle p-3 d-flex align-items-center justify-content-center font-weight-bold" style="width: 44px; height: 44px; font-size: 16px;">
                  4
                </div>
              </div>
              <div class="flex-grow-1 border-bottom border-secondary pb-3">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <h5 class="text-white font-weight-bold mb-1">{{ isIndonesian ? 'Tetapkan Sanksi SOP & Misi Pemulihan' : 'Configure SOP Rules & Consequences' }}</h5>
                  <router-link :to="{ name: 'CreateConsequence' }" class="btn btn-outline-danger btn-sm mb-1 font-weight-bold">
                    <i class="mdi mdi-plus mr-1"></i>{{ isIndonesian ? 'Tetapkan Sanksi SOP' : 'Create Penalty' }}
                  </router-link>
                </div>
                <p class="text-muted small mb-0">
                  {{ isIndonesian ? 'Tetapkan sanksi transparan jika terjadi pelanggaran SOP kerja, serta sediakan Misi Pemulihan reputasi.' : 'Establish transparent policy consequences to curb delays and ensure reliable workflow accountability.' }}
                </p>
              </div>
            </div>

            <!-- Step 5 -->
            <div class="d-flex mb-2">
              <div class="mr-3">
                <div class="badge badge-info rounded-circle p-3 d-flex align-items-center justify-content-center font-weight-bold" style="width: 44px; height: 44px; font-size: 16px;">
                  5
                </div>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <h5 class="text-white font-weight-bold mb-1">{{ isIndonesian ? 'Isi Katalog Hadiah di Toko Hadiah' : 'Stock Perks in Reward Shop' }}</h5>
                  <router-link :to="{ name: 'CreateRedeem' }" class="btn btn-outline-info btn-sm mb-1 font-weight-bold">
                    <i class="mdi mdi-store mr-1"></i>{{ isIndonesian ? 'Tambah Hadiah Toko' : 'Add Store Perk' }}
                  </router-link>
                </div>
                <p class="text-muted small mb-0">
                  {{ isIndonesian ? 'Pastikan Toko Hadiah memiliki item menarik yang bisa ditukar dengan Gold (voucher kopi, cuti, perangkat kerja) agar motivasi tim selalu tinggi.' : 'Curate attractive physical and experiential rewards that make earning gold truly valuable.' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import config from '@/config/config';
import store from '@/store';
import { storeReward } from '@/services/rewardService';
import { storeConsequence } from '@/services/consequenceService';
import { storeCompensation } from '@/services/compensationService';
import { storeRedeem } from '@/services/redeemService';

export default {
  name: 'GameSettingsView',
  data() {
    return {
      activeTab: 'terminology',
      simulatorXp: 350,
      isSaving: false,
      logoFile: null,
      logoPreview: null,
      companyForm: {
        name: '',
        theme_color: '#ffab00',
        welcome_message: '',
        logo: '',
      },
      engineConfig: {
        baseXp: 100,
        curveType: 'linear',
        dailyGoldCap: 150,
        streak7Bonus: 25,
        kudosAllowance: 5,
        kudosGold: 5,
        antiCheatLevel: 'balanced',
      },
      // AI Copilot state
      selectedArchetype: 'tech',
      aiPrompt: '',
      isGeneratingAi: false,
      isDeploying: false,
      activeBlueprint: null,
      hasCustomAiBlueprint: false,
    };
  },
  computed: {
    isIndonesian() {
      return this.$currentLocale === 'id';
    },
    promptPresets() {
      if (this.isIndonesian) {
        return [
          {
            key: 'saas',
            icon: 'mdi mdi-rocket-launch-outline',
            label: 'Startup SaaS 20 Orang',
            prompt: 'Kami startup SaaS 20 orang membutuhkan misi sprint delivery dan SOP code review PR',
          },
          {
            key: 'sales',
            icon: 'mdi mdi-briefcase-outline',
            label: 'Tim Penjualan B2B',
            prompt: 'Tim penjualan 10 orang fokus penyelesaian kontrak B2B dan pembaruan pipeline CRM',
          },
          {
            key: 'support',
            icon: 'mdi mdi-headset',
            label: 'Layanan Pelanggan 24/7',
            prompt: 'Layanan pelanggan 24/7 fokus waktu tanggap di bawah 15 menit dan CSAT 5 bintang',
          },
          {
            key: 'economy',
            icon: 'mdi mdi-scale-balance',
            label: 'Seimbangkan Ekonomi Gold',
            prompt: 'Seimbangkan ekonomi Gold dan XP agar anggaran toko hadiah tetap terkendali',
          },
        ];
      }
      return [
        {
          key: 'saas',
          icon: 'mdi mdi-rocket-launch-outline',
          label: '20-Person SaaS Startup',
          prompt: 'We are a 20-person SaaS startup needing sprint delivery quests and PR review SOPs',
        },
        {
          key: 'sales',
          icon: 'mdi mdi-briefcase-outline',
          label: 'B2B Sales Closing Team',
          prompt: '10-person sales team focused on B2B contract closing and CRM pipeline hygiene',
        },
        {
          key: 'support',
          icon: 'mdi mdi-headset',
          label: '24/7 Fast Support Team',
          prompt: '24/7 customer support focused on sub-15-minute response time and 5-star CSAT',
        },
        {
          key: 'economy',
          icon: 'mdi mdi-scale-balance',
          label: 'Balance Gold Economy',
          prompt: 'Balance Gold and XP economy so the reward shop budget stays sustainable',
        },
      ];
    },
    blueprintsLibrary() {
      if (this.isIndonesian) {
        return {
          tech: {
            title: 'Cetak Biru Utama: Rekayasa Perangkat Lunak & DevOps',
            archetypeLabel: 'Teknologi & Pengembangan Produk',
            healthScore: 96,
            engine: { baseXp: 100, dailyGoldCap: 200, streak7Bonus: 25, antiCheatLevel: 'strict' },
            quests: [
              {
                name: 'Tinjau 2 Pull Request Rekan Tim',
                frequency: 'Harian',
                type: 'always_active',
                gold: 25,
                xp: 100,
                description: 'Lakukan peninjauan kode secara konstruktif dan setujui PR rekan kerja untuk mempercepat alur rilis.',
                evidence: 'Tautan PR GitHub atau GitLab yang telah ditinjau',
              },
              {
                name: 'Tuntaskan Tugas Sprint Tanpa Hambatan',
                frequency: 'Mingguan',
                type: 'onetime',
                gold: 100,
                xp: 350,
                description: 'Selesaikan tiket pengembangan sprint sebelum batas akhir tinjauan sprint hari Jumat.',
                evidence: 'Tangkapan layar papan tugas berstatus Selesai',
              },
              {
                name: 'Rilis Produksi Tanpa Gangguan Layanan',
                frequency: 'Target Kuartal',
                type: 'onetime',
                gold: 250,
                xp: 800,
                description: 'Jalankan perilisan modul fitur utama ke server produksi dengan status pemeriksaan sistem normal.',
                evidence: 'Log pipeline CI/CD berstatus berhasil',
              },
            ],
            consequences: [
              {
                name: 'Gabung Kode ke Cabang Utama Tanpa Persetujuan PR',
                gold: 50,
                punishment: 'gold',
                description: 'Melewati proteksi cabang tanpa tinjauan rekan tim melanggar SOP keamanan kode.',
              },
              {
                name: 'Terlambat Menghadiri Rapat Harian >15 Menit',
                gold: 15,
                punishment: 'gold',
                description: 'Keterlambatan tanpa konfirmasi sebelumnya mengganggu sinkronisasi prioritas sprint tim.',
              },
            ],
            compensations: [
              {
                name: 'Sesi Berbagi Pengetahuan Teknis 15 Menit',
                description: 'Bawakan sesi presentasi singkat 15 menit kepada tim teknis untuk memulihkan catatan pelanggaran.',
              },
              {
                name: 'Perbarui Dokumentasi README & SOP Modul',
                description: 'Tuliskan dokumentasi arsitektur atau panduan instalasi layanan baru di basis pengetahuan tim.',
              },
            ],
            rewards: [
              { name: 'Voucher Kopi Pilihan Rp 50.000', gold: 50, category: 'Makanan & Minuman' },
              { name: '1 Hari Kerja Jarak Jauh Pilihan', gold: 200, category: 'Fasilitas Kerja' },
              { name: 'Buku Teknis / Langganan Kursus 1 Bulan', gold: 350, category: 'Anggaran Belajar' },
            ],
          },
          sales: {
            title: 'Cetak Biru Utama: Penjualan B2B & Pertumbuhan Pendapatan',
            archetypeLabel: 'Penjualan & Pengembangan Bisnis',
            healthScore: 94,
            engine: { baseXp: 80, dailyGoldCap: 300, streak7Bonus: 30, antiCheatLevel: 'balanced' },
            quests: [
              {
                name: 'Perbarui 5 Prospek & Catatan Panggilan di CRM',
                frequency: 'Harian',
                type: 'always_active',
                gold: 25,
                xp: 80,
                description: 'Jaga kerapian data CRM dengan mengisi catatan tindak lanjut dan tahap negosiasi klien.',
                evidence: 'Tangkapan layar log aktivitas CRM hari ini',
              },
              {
                name: 'Capai 3 Jadwal Presentasi Klien Baru',
                frequency: 'Mingguan',
                type: 'onetime',
                gold: 120,
                xp: 400,
                description: 'Lakukan minimal 3 presentasi proposal solusi kepada calon prospek korporat.',
                evidence: 'Undangan kalender terkonfirmasi dan tautan materi proposal',
              },
              {
                name: 'Penandatanganan Kontrak Klien Korporat Baru',
                frequency: 'Target Kuartal',
                type: 'onetime',
                gold: 300,
                xp: 1000,
                description: 'Selesaikan penandatanganan kontrak kerja sama dan faktur pembayaran tahap pertama.',
                evidence: 'Dokumen kontrak bertanda tangan resmi',
              },
            ],
            consequences: [
              {
                name: 'Prospek Tidak Diperbarui >7 Hari Tanpa Tindak Lanjut',
                gold: 30,
                punishment: 'gold',
                description: 'Membiarkan prospek potensial tanpa pembaruan menghambat pencapaian target tim.',
              },
              {
                name: 'Tidak Menghadiri Evaluasi Penjualan Mingguan',
                gold: 20,
                punishment: 'gold',
                description: 'Ketidakhadiran dalam evaluasi target mingguan tanpa pemberitahuan kepada atasan.',
              },
            ],
            compensations: [
              {
                name: 'Bagikan Studi Kasus Negosiasi ke Rekan Tim',
                description: 'Paparkan strategi penanganan keberatan klien pada sesi koordinasi pagi.',
              },
              {
                name: 'Riset 15 Kontak Prospek B2B Baru Terverifikasi',
                description: 'Tambahkan data prospek baru yang relevan ke dalam CRM untuk ditindaklanjuti tim.',
              },
            ],
            rewards: [
              { name: 'Voucher Bahan Bakar / Transportasi Rp 100.000', gold: 100, category: 'Tunjangan Perjalanan' },
              { name: 'Sesi Makan Siang & Diskusi Bersama Direksi', gold: 250, category: 'Mentoring Eksekutif' },
              { name: 'Pulang Lebih Awal 2 Jam di Hari Jumat', gold: 150, category: 'Keseimbangan Kerja' },
            ],
          },
          support: {
            title: 'Cetak Biru Utama: Layanan Pelanggan & Kepuasan Pengguna',
            archetypeLabel: 'Layanan Pelanggan & Operasional',
            healthScore: 98,
            engine: { baseXp: 90, dailyGoldCap: 150, streak7Bonus: 20, antiCheatLevel: 'balanced' },
            quests: [
              {
                name: 'Selesaikan 15 Tiket Bantuan dengan Penilaian Penuh',
                frequency: 'Harian',
                type: 'always_active',
                gold: 30,
                xp: 90,
                description: 'Berikan solusi cepat, sopan, dan tuntas pada setiap tiket kendala pelanggan.',
                evidence: 'Laporan penilaian tiket dari sistem layanan pelanggan',
              },
              {
                name: 'Waktu Tanggap Awal <15 Menit Sepanjang Minggu',
                frequency: 'Mingguan',
                type: 'onetime',
                gold: 100,
                xp: 350,
                description: 'Pastikan waktu tanggap rata-rata pesan masuk tidak melewati standar layanan perusahaan.',
                evidence: 'Ringkasan waktu tanggap mingguan dari dasbor bantuan',
              },
              {
                name: 'Tulis 2 Artikel Panduan Baru di Pusat Bantuan',
                frequency: 'Target Kuartal',
                type: 'onetime',
                gold: 150,
                xp: 500,
                description: 'Dokumentasikan panduan penyelesaian kendala umum yang sering ditanyakan pengguna.',
                evidence: 'Tautan artikel pusat bantuan yang telah terbit',
              },
            ],
            consequences: [
              {
                name: 'Tiket Melewati Batas Waktu Layanan Tanpa Eskalasi',
                gold: 25,
                punishment: 'gold',
                description: 'Membiarkan keluhan pelanggan melewati batas waktu tanpa eskalasi ke tim teknis.',
              },
              {
                name: 'Keluhan Pelanggan Akibat Etika Komunikasi',
                gold: 40,
                punishment: 'gold',
                description: 'Pelanggaran standar etika komunikasi resmi saat melayani pelanggan.',
              },
            ],
            compensations: [
              {
                name: 'Bantu Selesaikan 5 Tiket Eskalasi Rekan Tim',
                description: 'Bantu menangani antrean tiket rekan yang bertugas pada jam sibuk.',
              },
              {
                name: 'Perbarui 3 Templat Balasan Standar Layanan',
                description: 'Perbaiki kalimat templat jawaban tiket agar lebih jelas dan membantu.',
              },
            ],
            rewards: [
              { name: 'Voucher Belanja Rp 50.000', gold: 50, category: 'Voucher Belanja' },
              { name: 'Headset Kerja Ergonomis', gold: 200, category: 'Perlengkapan Kerja' },
              { name: 'Tambahan Cuti Tahunan 1 Hari', gold: 400, category: 'Fasilitas Cuti' },
            ],
          },
          creative: {
            title: 'Cetak Biru Utama: Agensi Kreatif & Produksi Konten',
            archetypeLabel: 'Kreatif & Pemasaran Digital',
            healthScore: 95,
            engine: { baseXp: 100, dailyGoldCap: 200, streak7Bonus: 25, antiCheatLevel: 'balanced' },
            quests: [
              {
                name: 'Terbitkan 2 Konten Media Sosial Sesuai Jadwal',
                frequency: 'Harian',
                type: 'always_active',
                gold: 30,
                xp: 100,
                description: 'Siapkan desain visual, naskah, dan penjadwalan konten sesuai kalender editorial.',
                evidence: 'Tautan unggahan yang telah tayang',
              },
              {
                name: 'Aset Visual Kampanye Disetujui Maksimal 1 Revisi',
                frequency: 'Mingguan',
                type: 'onetime',
                gold: 120,
                xp: 400,
                description: 'Serahkan aset visual kampanye utama yang disetujui pemangku kepentingan dalam maksimal satu revisi.',
                evidence: 'Tautan desain dan bukti persetujuan tertulis',
              },
              {
                name: 'Peluncuran Kampanye Kreatif Lintas Kanal',
                frequency: 'Target Kuartal',
                type: 'onetime',
                gold: 300,
                xp: 900,
                description: 'Selesaikan produksi penuh materi kampanye kuartalan tepat pada tanggal peluncuran.',
                evidence: 'Laporan penayangan materi kampanye',
              },
            ],
            consequences: [
              {
                name: 'Keterlambatan Penyerahan Materi Kampanye',
                gold: 35,
                punishment: 'gold',
                description: 'Gagal menyerahkan materi siap tayang sesuai tenggat jadwal publikasi.',
              },
              {
                name: 'Kesalahan Penulisan Nama Merek atau Logo Resmi',
                gold: 25,
                punishment: 'gold',
                description: 'Kelalaian pemeriksaan kualitas visual dan teks sebelum materi dipublikasikan.',
              },
            ],
            compensations: [
              {
                name: 'Rancang 5 Templat Desain Siap Pakai',
                description: 'Tambahkan 5 templat grafis standar ke pustaka aset bersama tim.',
              },
              {
                name: 'Bawakan Sesi Singkat Panduan Desain Sistem',
                description: 'Bagikan kiat kerapian komponen visual kepada anggota tim kreatif.',
              },
            ],
            rewards: [
              { name: 'Langganan Aplikasi Kreatif 1 Bulan', gold: 70, category: 'Langganan Digital' },
              { name: 'Voucher Tiket Bioskop 2 Orang', gold: 120, category: 'Hiburan' },
              { name: 'Kerja Dari Mana Saja 2 Hari Pilihan', gold: 250, category: 'Fleksibilitas Kerja' },
            ],
          },
          operations: {
            title: 'Cetak Biru Utama: Operasional Logistik & Rantai Pasok',
            archetypeLabel: 'Operasional & Logistik',
            healthScore: 97,
            engine: { baseXp: 100, dailyGoldCap: 180, streak7Bonus: 20, antiCheatLevel: 'strict' },
            quests: [
              {
                name: 'Pemeriksaan Fisik Stok & Pencatatan Gudang Akurat',
                frequency: 'Harian',
                type: 'always_active',
                gold: 25,
                xp: 100,
                description: 'Pastikan pencatatan barang masuk dan keluar di gudang sesuai tanpa selisih.',
                evidence: 'Formulir pemeriksaan stok harian bertanda tangan',
              },
              {
                name: 'Pengiriman Tepat Waktu Selama 7 Hari Berturut-turut',
                frequency: 'Mingguan',
                type: 'onetime',
                gold: 110,
                xp: 380,
                description: 'Seluruh paket pesanan diserahkan ke armada pengiriman sebelum batas jam operasional.',
                evidence: 'Rekapitulasi nomor resi dan waktu serah terima',
              },
              {
                name: 'Inspeksi Keselamatan Kerja & Kerapian Gudang',
                frequency: 'Target Kuartal',
                type: 'onetime',
                gold: 200,
                xp: 600,
                description: 'Lulus pemeriksaan standar keselamatan kerja dan penataan rak penyimpanan.',
                evidence: 'Berita acara inspeksi keselamatan kerja internal',
              },
            ],
            consequences: [
              {
                name: 'Kesalahan Label Pengiriman Akibat Kelalaian Kemas',
                gold: 40,
                punishment: 'gold',
                description: 'Kesalahan penempelan label resi yang menyebabkan pengiriman salah tujuan.',
              },
              {
                name: 'Tidak Mengenakan Alat Pelindung Diri di Area Gudang',
                gold: 20,
                punishment: 'gold',
                description: 'Pelanggaran protokol keselamatan kerja di area operasional.',
              },
            ],
            compensations: [
              {
                name: 'Rapikan dan Tata Ulang 3 Baris Rak Penyimpanan',
                description: 'Lakukan penataan ulang area penyimpanan material sesuai label kategori.',
              },
              {
                name: 'Periksa Kalibrasi Timbangan & Peralatan Ukur',
                description: 'Pastikan seluruh peralatan ukur gudang bekerja akurat dan tercatat.',
              },
            ],
            rewards: [
              { name: 'Voucher Belanja Kebutuhan Harian Rp 100.000', gold: 100, category: 'Voucher Rumah Tangga' },
              { name: 'Botol Minum Tahan Suhu', gold: 180, category: 'Perlengkapan' },
              { name: 'Voucher Makan Siang Tambahan', gold: 80, category: 'Makanan & Minuman' },
            ],
          },
        };
      }

      return {
        tech: {
          title: 'Master Blueprint: Software Engineering & DevOps Team',
          archetypeLabel: 'Technology & Product Development',
          healthScore: 96,
          engine: { baseXp: 100, dailyGoldCap: 200, streak7Bonus: 25, antiCheatLevel: 'strict' },
          quests: [
            {
              name: 'Review 2 Teammate Pull Requests',
              frequency: 'Daily',
              type: 'always_active',
              gold: 25,
              xp: 100,
              description: 'Provide constructive code reviews and approve peer pull requests to keep delivery moving.',
              evidence: 'Reviewed GitHub or GitLab pull request URL',
            },
            {
              name: 'Complete Sprint Tickets Without Blockers',
              frequency: 'Weekly',
              type: 'onetime',
              gold: 100,
              xp: 350,
              description: 'Finish assigned sprint development tickets ahead of Friday sprint review.',
              evidence: 'Issue tracker screenshot showing Done status',
            },
            {
              name: 'Execute Zero-Downtime Production Release',
              frequency: 'Quarterly Milestone',
              type: 'onetime',
              gold: 250,
              xp: 800,
              description: 'Ship a core feature release to production with passing service health checks.',
              evidence: 'CI/CD deployment log showing Succeeded status',
            },
          ],
          consequences: [
            {
              name: 'Merge to Main Branch Without PR Approval',
              gold: 50,
              punishment: 'gold',
              description: 'Bypassing branch protection without peer review violates repository security SOPs.',
            },
            {
              name: 'Late to Daily Standup >15 Minutes Without Notice',
              gold: 15,
              punishment: 'gold',
              description: 'Unannounced lateness disrupts daily sprint priority alignment.',
            },
          ],
          compensations: [
            {
              name: 'Deliver 15-Minute Engineering Knowledge Session',
              description: 'Host a concise 15-minute refactoring or debugging walkthrough for the engineering team.',
            },
            {
              name: 'Update Module README & Setup Documentation',
              description: 'Document service architecture or onboarding setup steps in the team knowledge base.',
            },
          ],
          rewards: [
            { name: 'Specialty Coffee Shop Voucher', gold: 50, category: 'Food & Beverage' },
            { name: '1 Flexible Remote Work Day', gold: 200, category: 'Workplace Perk' },
            { name: 'Technical Book or 1-Month Course Pass', gold: 350, category: 'Learning Budget' },
          ],
        },
        sales: {
          title: 'Master Blueprint: B2B Sales & Revenue Growth',
          archetypeLabel: 'Sales & Business Development',
          healthScore: 94,
          engine: { baseXp: 80, dailyGoldCap: 300, streak7Bonus: 30, antiCheatLevel: 'balanced' },
          quests: [
            {
              name: 'Update 5 CRM Pipeline Deals & Call Logs',
              frequency: 'Daily',
              type: 'always_active',
              gold: 25,
              xp: 80,
              description: 'Maintain CRM data hygiene with accurate follow-up notes and deal stages.',
              evidence: 'Screenshot of today\'s CRM activity log',
            },
            {
              name: 'Deliver 3 Qualified Prospect Solution Demos',
              frequency: 'Weekly',
              type: 'onetime',
              gold: 120,
              xp: 400,
              description: 'Present tailored solution proposals to at least 3 qualified enterprise prospects.',
              evidence: 'Confirmed calendar invites and proposal deck link',
            },
            {
              name: 'Close New Enterprise Client Contract',
              frequency: 'Quarterly Milestone',
              type: 'onetime',
              gold: 300,
              xp: 1000,
              description: 'Secure a signed service agreement and initial invoice settlement.',
              evidence: 'Countersigned digital contract record',
            },
          ],
          consequences: [
            {
              name: 'Pipeline Deal Stagnant >7 Days Without Follow-Up',
              gold: 30,
              punishment: 'gold',
              description: 'Leaving active prospects without documented follow-up slows team revenue execution.',
            },
            {
              name: 'Unexcused Absence from Weekly Pipeline Sync',
              gold: 20,
              punishment: 'gold',
              description: 'Missing the weekly pipeline review without prior manager notice.',
            },
          ],
          compensations: [
            {
              name: 'Share Successful Objection-Handling Case Study',
              description: 'Present practical negotiation lessons during the morning sales huddle.',
            },
            {
              name: 'Research 15 Verified New B2B Prospect Contacts',
              description: 'Add verified decision-maker leads into the shared CRM prospecting pool.',
            },
          ],
          rewards: [
            { name: 'Commuter & Fuel Allowance Voucher', gold: 100, category: 'Travel Allowance' },
            { name: 'Executive Mentorship Lunch Session', gold: 250, category: 'Executive Mentorship' },
            { name: 'Early Friday Sign-Off (2 Hours)', gold: 150, category: 'Lifestyle Perk' },
          ],
        },
        support: {
          title: 'Master Blueprint: Customer Support & CSAT Operations',
          archetypeLabel: 'Customer Support & Operations',
          healthScore: 98,
          engine: { baseXp: 90, dailyGoldCap: 150, streak7Bonus: 20, antiCheatLevel: 'balanced' },
          quests: [
            {
              name: 'Resolve 15 Support Tickets with Full CSAT Score',
              frequency: 'Daily',
              type: 'always_active',
              gold: 30,
              xp: 90,
              description: 'Deliver clear, courteous, and complete resolutions across assigned customer tickets.',
              evidence: 'Helpdesk ticket rating summary report',
            },
            {
              name: 'Maintain Sub-15-Minute First Response All Week',
              frequency: 'Weekly',
              type: 'onetime',
              gold: 100,
              xp: 350,
              description: 'Keep average first-response time within the company service threshold.',
              evidence: 'Weekly response time report from helpdesk dashboard',
            },
            {
              name: 'Publish 2 New Help Center Troubleshooting Guides',
              frequency: 'Quarterly Milestone',
              type: 'onetime',
              gold: 150,
              xp: 500,
              description: 'Document clear step-by-step guides for recurring customer questions.',
              evidence: 'Published knowledge base article links',
            },
          ],
          consequences: [
            {
              name: 'Ticket Breaches Response Target Without Escalation',
              gold: 25,
              punishment: 'gold',
              description: 'Allowing an open ticket to exceed response thresholds without tier-2 escalation.',
            },
            {
              name: 'Verified Communication Etiquette Escalation',
              gold: 40,
              punishment: 'gold',
              description: 'Violation of official customer communication standards.',
            },
          ],
          compensations: [
            {
              name: 'Assist Teammates on 5 Escalated Queue Tickets',
              description: 'Help clear complex backlog tickets during peak support hours.',
            },
            {
              name: 'Audit and Improve 3 Canned Response Macros',
              description: 'Refine support reply templates for clarity and accuracy.',
            },
          ],
          rewards: [
            { name: 'Retail Shopping Gift Voucher', gold: 50, category: 'Shopping' },
            { name: 'Ergonomic Wireless Work Headset', gold: 200, category: 'Equipment Perk' },
            { name: '+1 Additional Annual Leave Day', gold: 400, category: 'Leave Perk' },
          ],
        },
        creative: {
          title: 'Master Blueprint: Creative Agency & Content Studio',
          archetypeLabel: 'Creative & Digital Marketing',
          healthScore: 95,
          engine: { baseXp: 100, dailyGoldCap: 200, streak7Bonus: 25, antiCheatLevel: 'balanced' },
          quests: [
            {
              name: 'Publish 2 Scheduled Social Assets On Time',
              frequency: 'Daily',
              type: 'always_active',
              gold: 30,
              xp: 100,
              description: 'Complete visual design, copy, and publishing in line with the editorial calendar.',
              evidence: 'Live published post URLs',
            },
            {
              name: 'Campaign Key Visual Approved Within 1 Revision',
              frequency: 'Weekly',
              type: 'onetime',
              gold: 120,
              xp: 400,
              description: 'Deliver primary campaign visuals approved by stakeholders in one revision round or less.',
              evidence: 'Design file link and written stakeholder sign-off',
            },
            {
              name: 'Deliver Full Multi-Channel Quarterly Campaign Kit',
              frequency: 'Quarterly Milestone',
              type: 'onetime',
              gold: 300,
              xp: 900,
              description: 'Ship all creative assets for a quarterly launch on schedule.',
              evidence: 'Published campaign asset delivery report',
            },
          ],
          consequences: [
            {
              name: 'Late Campaign Deliverable Past Scheduled Slot',
              gold: 35,
              punishment: 'gold',
              description: 'Missing agreed publishing deadlines for approved campaign slots.',
            },
            {
              name: 'Brand Spelling or Official Logo Misuse Error',
              gold: 25,
              punishment: 'gold',
              description: 'Skipping quality checks prior to external publication.',
            },
          ],
          compensations: [
            {
              name: 'Create 5 Reusable Brand Design Templates',
              description: 'Contribute 5 ready-to-use visual templates to the shared design library.',
            },
            {
              name: 'Host 15-Minute Design System Consistency Walkthrough',
              description: 'Share practical component hygiene tips with the creative team.',
            },
          ],
          rewards: [
            { name: '1-Month Creative Tool Subscription Pass', gold: 70, category: 'Digital Subscription' },
            { name: 'Cinema Ticket Pair Voucher', gold: 120, category: 'Entertainment' },
            { name: '2 Flexible Work-From-Anywhere Days', gold: 250, category: 'Workplace Flexibility' },
          ],
        },
        operations: {
          title: 'Master Blueprint: Logistics & Supply Chain Operations',
          archetypeLabel: 'Operations & Logistics',
          healthScore: 97,
          engine: { baseXp: 100, dailyGoldCap: 180, streak7Bonus: 20, antiCheatLevel: 'strict' },
          quests: [
            {
              name: 'Complete Daily Physical Stock & Ledger Reconciliation',
              frequency: 'Daily',
              type: 'always_active',
              gold: 25,
              xp: 100,
              description: 'Verify inbound and outbound warehouse inventory records with zero discrepancy.',
              evidence: 'Signed daily stock count checklist',
            },
            {
              name: 'Maintain On-Time Dispatch Across 7 Consecutive Days',
              frequency: 'Weekly',
              type: 'onetime',
              gold: 110,
              xp: 380,
              description: 'Hand over all scheduled shipments to carriers ahead of daily cut-off times.',
              evidence: 'Shipment manifest and carrier handover timestamps',
            },
            {
              name: 'Pass Quarterly Warehouse Safety & 5S Audit',
              frequency: 'Quarterly Milestone',
              type: 'onetime',
              gold: 200,
              xp: 600,
              description: 'Complete full workplace safety and storage rack organization inspection.',
              evidence: 'Signed internal safety inspection report',
            },
          ],
          consequences: [
            {
              name: 'Mislabelled Shipment Due to Packing Oversight',
              gold: 40,
              punishment: 'gold',
              description: 'Applying an incorrect shipping label that causes delivery misrouting.',
            },
            {
              name: 'Entering Warehouse Zone Without Required Safety Gear',
              gold: 20,
              punishment: 'gold',
              description: 'Violating mandatory personal protective equipment rules in operational zones.',
            },
          ],
          compensations: [
            {
              name: 'Reorganize and Relabel 3 Storage Rack Aisles',
              description: 'Restore clean material categorization across warehouse storage bays.',
            },
            {
              name: 'Verify Calibration on Warehouse Weighing Equipment',
              description: 'Check and log measurement accuracy across operational scales.',
            },
          ],
          rewards: [
            { name: 'Supermarket Household Voucher', gold: 100, category: 'Household Voucher' },
            { name: 'Insulated Stainless Steel Tumbler', gold: 180, category: 'Merchandise' },
            { name: 'Extra Team Lunch Voucher', gold: 80, category: 'Food & Beverage' },
          ],
        },
      };
    },
    progressionMatrix() {
      const base = this.engineConfig.baseXp || 100;
      const curve = this.engineConfig.curveType || 'linear';
      const levels = [];
      let cumXp = 0;
      for (let lvl = 1; lvl <= 6; lvl++) {
        let xpReq = 0;
        if (curve === 'linear') {
          xpReq = lvl * base;
        } else if (curve === 'moderate') {
          xpReq = Math.round(Math.pow(lvl, 1.25) * base);
        } else {
          xpReq = Math.round(Math.pow(lvl, 1.5) * base);
        }
        cumXp += xpReq;
        const goldBonus = Math.round(20 + (lvl * 10));
        levels.push({
          tier: `Lvl ${lvl} → ${lvl + 1}`,
          badgeClass: lvl <= 2 ? 'badge-primary' : (lvl <= 4 ? 'badge-warning' : 'badge-danger'),
          xpReq,
          cumXp,
          goldBonus: `+${goldBonus} Gold`,
        });
      }
      return levels;
    },
    calculatedSim() {
      const total = Math.max(0, parseInt(this.simulatorXp, 10) || 0);
      const base = this.engineConfig.baseXp || 100;
      const curve = this.engineConfig.curveType || 'linear';
      let level = 1;
      let remaining = total;
      
      const getReq = (lvl) => {
        if (curve === 'linear') return lvl * base;
        if (curve === 'moderate') return Math.round(Math.pow(lvl, 1.25) * base);
        return Math.round(Math.pow(lvl, 1.5) * base);
      };

      let xpForNext = getReq(level);
      while (remaining >= xpForNext && level < 50) {
        remaining -= xpForNext;
        level += 1;
        xpForNext = getReq(level);
      }

      const percent = Math.min(100, Math.round((remaining / xpForNext) * 100));
      return {
        level,
        currentXp: remaining,
        xpNext: xpForNext,
        percent,
      };
    },
  },
  watch: {
    '$currentLocale'() {
      if (!this.hasCustomAiBlueprint && this.blueprintsLibrary[this.selectedArchetype]) {
        this.activeBlueprint = this.blueprintsLibrary[this.selectedArchetype];
      }
    },
  },
  mounted() {
    this.fetchCompanyProfile();
    this.loadEngineConfig();
    this.activeBlueprint = this.blueprintsLibrary.tech;
  },
  methods: {
    formatLocaleNumber(val) {
      return Number(val || 0).toLocaleString(this.isIndonesian ? 'id-ID' : 'en-US');
    },
    formatAntiCheatLabel(level) {
      const key = String(level || 'balanced').toLowerCase();
      if (key === 'strict') return this.$t('gameSettings.antiCheatShortStrict');
      if (key === 'lenient') return this.$t('gameSettings.antiCheatShortLenient');
      return this.$t('gameSettings.antiCheatShortBalanced');
    },
    async loadEngineConfig() {
      try {
        const saved = localStorage.getItem('gamiteam_engine_settings');
        if (saved) {
          this.engineConfig = Object.assign({}, this.engineConfig, JSON.parse(saved));
        }
      } catch (e) {
        console.error('Error loading local engine config:', e);
      }

      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) return;
      try {
        const resp = await axios.get(`${config.API_URL}game-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const remoteConfig = resp.data?.data || resp.data;
        if (remoteConfig && typeof remoteConfig === 'object' && Object.keys(remoteConfig).length > 0) {
          this.engineConfig = Object.assign({}, this.engineConfig, remoteConfig);
          localStorage.setItem('gamiteam_engine_settings', JSON.stringify(this.engineConfig));
        }
      } catch (e) {
        // Fallback to localStorage config already loaded
      }
    },
    async saveEngineConfig() {
      try {
        localStorage.setItem('gamiteam_engine_settings', JSON.stringify(this.engineConfig));
        const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
        if (token) {
          try {
            await axios.post(`${config.API_URL}game-settings`, this.engineConfig, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (apiErr) {
            console.warn('Saved engine config locally; backend sync warning:', apiErr?.message);
          }
        }
        Swal.fire({
          icon: 'success',
          title: this.$t('common.success'),
          text: this.$t('gameSettings.engineSaved'),
          confirmButtonColor: '#ffab00',
        });
      } catch (e) {
        console.error(e);
      }
    },
    selectArchetype(type) {
      this.selectedArchetype = type;
      this.hasCustomAiBlueprint = false;
      if (this.blueprintsLibrary[type]) {
        this.activeBlueprint = this.blueprintsLibrary[type];
      }
    },
    setPrompt(text) {
      this.aiPrompt = text;
    },
    async generateAiBlueprint() {
      this.isGeneratingAi = true;
      const text = (this.aiPrompt || '').toLowerCase();
      let targetKey = 'tech';
      if (text.includes('sales') || text.includes('penjualan') || text.includes('closing') || text.includes('b2b')) {
        targetKey = 'sales';
      } else if (text.includes('cs') || text.includes('support') || text.includes('pelanggan')) {
        targetKey = 'support';
      } else if (text.includes('kreatif') || text.includes('creative') || text.includes('agency') || text.includes('konten') || text.includes('desain')) {
        targetKey = 'creative';
      } else if (text.includes('gudang') || text.includes('logistik') || text.includes('logistics') || text.includes('operasional')) {
        targetKey = 'operations';
      } else if (this.blueprintsLibrary[this.selectedArchetype]) {
        targetKey = this.selectedArchetype;
      }

      this.selectedArchetype = targetKey;
      const baseBlueprint = JSON.parse(JSON.stringify(this.blueprintsLibrary[targetKey]));
      let customized = false;

      const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
      try {
        if (token) {
          const resp = await axios.post(
            `${config.API_URL}ai/generate-rules`,
            {
              goal: this.aiPrompt || baseBlueprint.archetypeLabel,
              prompt: this.aiPrompt || baseBlueprint.archetypeLabel,
              archetype: targetKey,
              division: baseBlueprint.archetypeLabel,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const aiData = resp.data?.data || resp.data;
          if (aiData && typeof aiData === 'object') {
            const quests = aiData.quests || aiData.rules || [];
            if (Array.isArray(quests) && quests.length > 0) {
              baseBlueprint.quests = quests.map((item) => ({
                name: item.name || item.title,
                frequency: item.frequency || this.$t('gameSettings.freqDaily'),
                type: item.type === 'onetime' ? 'onetime' : 'always_active',
                gold: item.gold || 50,
                xp: item.xp || item.xp_reward || 150,
                description: item.description || this.aiPrompt,
                evidence: item.evidence || this.$t('gameSettings.defaultEvidenceHint'),
              }));
              customized = true;
            } else if (aiData.name || aiData.title) {
              baseBlueprint.quests.unshift({
                name: aiData.name || aiData.title,
                frequency: this.$t('gameSettings.freqDaily'),
                type: 'always_active',
                gold: aiData.gold || 50,
                xp: aiData.xp || 150,
                description: aiData.description || this.aiPrompt,
                evidence: aiData.evidence || this.$t('gameSettings.defaultEvidenceHint'),
              });
              customized = true;
            }
            if (aiData.engine) {
              baseBlueprint.engine = Object.assign({}, baseBlueprint.engine, aiData.engine);
            }
          }
        }
      } catch (err) {
        // Fallback cleanly to localized blueprintsLibrary
      } finally {
        this.hasCustomAiBlueprint = customized;
        this.activeBlueprint = baseBlueprint;
        this.isGeneratingAi = false;

        Swal.fire({
          icon: 'success',
          title: this.$t('gameSettings.aiGeneratedTitle'),
          text: this.$t('gameSettings.aiGeneratedDesc', {
            archetype: this.activeBlueprint.archetypeLabel,
            score: this.activeBlueprint.healthScore,
          }),
          confirmButtonColor: '#ffab00',
        });
      }
    },
    applyAiEngineSettings() {
      if (!this.activeBlueprint) return;
      this.engineConfig.baseXp = this.activeBlueprint.engine.baseXp;
      this.engineConfig.dailyGoldCap = this.activeBlueprint.engine.dailyGoldCap;
      this.engineConfig.streak7Bonus = this.activeBlueprint.engine.streak7Bonus;
      this.engineConfig.antiCheatLevel = this.activeBlueprint.engine.antiCheatLevel;
      this.saveEngineConfig();
      Swal.fire({
        icon: 'success',
        title: this.$t('common.success'),
        text: this.$t('gameSettings.aiAppliedToEngineDesc'),
        confirmButtonColor: '#3b82f6',
      });
    },
    async deployBlueprintToWorkspace() {
      if (!this.activeBlueprint) return;

      const confirmResult = await Swal.fire({
        title: this.$t('gameSettings.deployConfirmTitle'),
        text: this.$t('gameSettings.deployConfirmDesc', {
          quests: this.activeBlueprint.quests.length,
          consequences: this.activeBlueprint.consequences.length,
          compensations: this.activeBlueprint.compensations.length,
          rewards: this.activeBlueprint.rewards.length,
        }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6b7280',
        confirmButtonText: this.$t('gameSettings.deployConfirmBtn'),
        cancelButtonText: this.$t('common.cancel'),
      });

      if (!confirmResult.isConfirmed) return;

      this.isDeploying = true;
      try {
        // 1. Quests
        for (const q of this.activeBlueprint.quests) {
          try {
            await storeReward({
              name: q.name,
              gold: q.gold,
              type: q.type,
              repeat_type: 'daily',
              description: `${q.description} ${this.$t('gameSettings.evidenceGuideSuffix', { evidence: q.evidence })}`,
              assigned_to: 'all_player',
            });
          } catch (e) {
            console.warn('Deploy quest item warning:', e);
          }
        }

        // 2. Consequences
        for (const c of this.activeBlueprint.consequences) {
          try {
            await storeConsequence({
              name: c.name,
              gold: c.gold,
              punishment: c.punishment,
              description: c.description,
            });
          } catch (e) {
            console.warn('Deploy consequence item warning:', e);
          }
        }

        // 3. Compensations
        for (const comp of this.activeBlueprint.compensations) {
          try {
            await storeCompensation({
              name: comp.name,
              description: comp.description,
            });
          } catch (e) {
            console.warn('Deploy compensation item warning:', e);
          }
        }

        // 4. Reward Shop Items
        for (const r of this.activeBlueprint.rewards) {
          try {
            await storeRedeem({
              name: r.name,
              gold: r.gold,
            });
          } catch (e) {
            console.warn('Deploy redeem item warning:', e);
          }
        }

        // Also sync engine parameters
        if (this.activeBlueprint && this.activeBlueprint.engine) {
          this.engineConfig.baseXp = this.activeBlueprint.engine.baseXp;
          this.engineConfig.dailyGoldCap = this.activeBlueprint.engine.dailyGoldCap;
          this.engineConfig.streak7Bonus = this.activeBlueprint.engine.streak7Bonus;
          this.engineConfig.antiCheatLevel = this.activeBlueprint.engine.antiCheatLevel;
          localStorage.setItem('gamiteam_engine_settings', JSON.stringify(this.engineConfig));
          const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
          if (token) {
            axios.post(`${config.API_URL}game-settings`, this.engineConfig, {
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => {});
          }
        }

        Swal.fire({
          icon: 'success',
          title: this.$t('gameSettings.aiDeploySuccessTitle'),
          text: this.$t('gameSettings.aiDeployedSuccess'),
          confirmButtonColor: '#10b981',
        });
      } catch (err) {
        console.error('Error deploying blueprint:', err);
        Swal.fire({
          icon: 'error',
          title: this.$t('common.error'),
          text: this.$t('gameSettings.aiDeployPartialError'),
        });
      } finally {
        this.isDeploying = false;
      }
    },
    copyBlueprintJson() {
      if (!this.activeBlueprint) return;
      const json = JSON.stringify(this.activeBlueprint, null, 2);
      navigator.clipboard.writeText(json);
      Swal.fire({
        icon: 'success',
        title: this.$t('gameSettings.copiedTitle'),
        text: this.$t('gameSettings.copiedBlueprintDesc'),
        timer: 1500,
        showConfirmButton: false,
      });
    },
    async fetchCompanyProfile() {
      const token = store.state.authToken || localStorage.getItem('authToken');
      if (!token) return;

      try {
        const resp = await axios.get(`${config.API_URL}company/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const company = resp.data?.data || {};
        this.companyForm.name = company.name || '';
        this.companyForm.theme_color = company.theme_color || '#ffab00';
        this.companyForm.welcome_message = company.welcome_message || '';
        if (company.logo) {
          this.companyForm.logo = company.logo;
          this.logoPreview = company.logo.startsWith('http')
            ? company.logo
            : `${config.STORAGE_URL}${company.logo.replace(/^\/+/, '')}`;
        }
      } catch (err) {
        console.warn('Could not load company profile:', err?.response?.data || err.message);
      }
    },

    handleLogoChange(e) {
      const file = e.target.files?.[0];
      if (file) {
        this.logoFile = file;
        this.logoPreview = URL.createObjectURL(file);
      }
    },

    async saveCompanyProfile() {
      const token = store.state.authToken || localStorage.getItem('authToken');
      if (!token) return;

      this.isSaving = true;
      try {
        const formData = new FormData();
        formData.append('name', this.companyForm.name);
        formData.append('theme_color', this.companyForm.theme_color);
        formData.append('welcome_message', this.companyForm.welcome_message);
        if (this.logoFile) {
          formData.append('logo', this.logoFile);
        }

        const resp = await axios.post(`${config.API_URL}company/profile`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        Swal.fire({
          icon: 'success',
          title: this.$t('common.success'),
          text: this.isIndonesian ? 'Identitas dan preferensi perusahaan Anda telah berhasil diperbarui.' : 'Company branding and preferences updated successfully.',
          confirmButtonColor: '#0090e7',
        });

        if (resp.data?.data?.logo) {
          this.companyForm.logo = resp.data.data.logo;
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: this.$t('common.error'),
          text: err.response?.data?.message || (this.isIndonesian ? 'Gagal menyimpan pengaturan.' : 'Error saving settings.'),
          confirmButtonColor: '#0090e7',
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.game-settings-view .nav-tabs .nav-link {
  color: #a7afb7;
  border-bottom: 2px solid transparent;
  padding: 10px 18px;
}

.game-settings-view .nav-tabs .nav-link:hover {
  color: #ffab00;
  border-color: transparent;
}

.game-settings-view .nav-tabs .nav-link.active {
  color: #ffab00;
  background-color: transparent;
  border-color: transparent transparent #ffab00 transparent;
  border-bottom: 3px solid #ffab00;
}

.timeline-steps .badge {
  font-weight: 700;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
}

.last-border-0:last-child {
  border-bottom: 0 !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>
